import {
  AttackListSenderSettingRuleExistence,
  AttackListSenderSettingRuleGender,
  DiagnosisType,
} from '@/types/schema.generated'
import type {
  CreateAttackListSenderSettingsInput,
  CreateAttackListSenderSettingsInputSetting,
  InputMaybe,
} from '@/types/schema.generated'
import * as papaparse from 'papaparse'

const CSV_KEYS = [
  'ステータス',
  'OAのメールアドレス',
  'テンプレタイトル',
  '預貯金下限',
  '預貯金上限',
  '本人年収下限',
  '本人年収上限',
  '世帯年収下限',
  '世帯年収上限',
  'パートナー',
  '子供',
  '一人目の子供の年齢下限',
  '一人目の子供の年齢上限',
  '性別',
  '同一企業 追いアプローチ',
  '資産運用額下限',
  '資産運用額上限',
  '年齢下限',
  '年齢上限',
  '職業ID',
  'ローン残債下限',
  'ローン残債上限',
  '末子の年齢下限',
  '末子の年齢上限',
  '気になるトピックID',
  'アプローチ本文',
  'アプローチ選択肢1',
  'アプローチ選択肢2',
  'アプローチ選択肢3',
  'アプローチ選択肢4',
  'アプローチ選択肢5',
  'アプローチ選択肢6',
  '診断タイプ',
] as const

type CSVData = Record<(typeof CSV_KEYS)[number], string>

const mapExistence = (value: string): AttackListSenderSettingRuleExistence => {
  switch (value) {
    case 'いる':
      return AttackListSenderSettingRuleExistence.Exist
    case 'いない':
      return AttackListSenderSettingRuleExistence.NotExist
    case 'すべて':
      return AttackListSenderSettingRuleExistence.NotSpecified
    default:
      throw new Error(`パートナー・子供の有無の値が不正です。${value}`)
  }
}

const mapGender = (value: string): AttackListSenderSettingRuleGender => {
  switch (value) {
    case '男性':
      return AttackListSenderSettingRuleGender.Male
    case '女性':
      return AttackListSenderSettingRuleGender.Female
    case 'すべて':
      return AttackListSenderSettingRuleGender.NotSpecified
    default:
      throw new Error(`性別の値が不正です。${value}`)
  }
}

const mapSameCorporationApproach = (value: string): boolean => {
  switch (value) {
    case 'する':
      return true
    case 'しない':
      return false
    default:
      throw new Error(`同一企業 追いアプローチの値が不正です。${value}`)
  }
}

const validateEmail = (emails: string[]) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!emails.every(email => regex.test(email))) {
    throw new Error(`不正なメールアドレスが含まれています。${emails}`)
  }
  return
}

const mapApproachBody = (body: string) => {
  if (body === '') {
    throw new Error('アプローチ本文が空です。')
  }

  if (body === '#N/A') {
    throw new Error('アプローチ本文が不正です。')
  }

  return body
}

const mapSelections = (data: CSVData): string[] => {
  const rawSelections = [
    data['アプローチ選択肢1'],
    data['アプローチ選択肢2'],
    data['アプローチ選択肢3'],
    data['アプローチ選択肢4'],
    data['アプローチ選択肢5'],
    data['アプローチ選択肢6'],
  ]

  const filtered = rawSelections.filter(Boolean)
  if (filtered.length === 0) {
    throw new Error('選択肢が空です。')
  }

  const hasErrorField = filtered.some(selection => selection === '#N/A')
  if (hasErrorField) {
    throw new Error('選択肢が不正です。')
  }

  return filtered
}

type Range = {
  min: number | null
  max: number | null
}

type ColumnType =
  | '預貯金'
  | '本人年収'
  | '世帯年収'
  | '資産運用額'
  | 'ローン残債'

const mapValueRange = (
  min: string,
  max: string,
  columnType: ColumnType
): Range => {
  const parseValue = (value: string): number | null => {
    if (value === 'なし') return null

    const match = value.replaceAll(/,/g, '').match(/(\d+)(万|億)円/)
    if (!match) {
      throw new Error(`[${columnType}] の値が不正です: ${value}`)
    }

    const intValue = parseInt(match[1], 10)
    const unit = match[2] === '億' ? 100_000_000 : 10_000

    return intValue * unit
  }

  const minValue = parseValue(min)
  const maxValue = parseValue(max)
  if (minValue && maxValue && minValue > maxValue) {
    throw new Error(
      `[${columnType}] の下限値が上限値より大さいです。下限値: ${min}, 上限値: ${max}`
    )
  }

  return {
    min: minValue,
    max: maxValue,
  }
}

const mapAgeRange = (min: string, max: string): Range => {
  const parseValue = (value: string): number | null => {
    if (value === 'なし') return null

    if (isNaN(Number(value))) {
      throw new Error(`年齢の値が不正です。${value}`)
    }

    return Number(value)
  }

  const minAge = parseValue(min)
  const maxAge = parseValue(max)
  if (minAge && maxAge && minAge > maxAge) {
    throw new Error(
      `年齢の下限値が上限値より大さいです。下限値: ${min}, 上限値: ${max}`
    )
  }

  return {
    min: minAge,
    max: maxAge,
  }
}

const mapChildAgeRange = (min: string, max: string): Range => {
  const parseValue = (value: string): number | null => {
    if (value === 'なし') return null

    const match = value.match(/(\d+)歳/)
    if (!match) {
      throw new Error(`子供の年齢の値が不正です: ${value}`)
    }

    const age = Number(match[1])
    if (age < 0 || age > 20) {
      throw new Error(`子供の年齢の値に指定できるのは0~20歳までです。${value}`)
    }
    return age
  }

  const minAge = parseValue(min)
  const maxAge = parseValue(max)
  if (minAge && maxAge && minAge > maxAge) {
    throw new Error(
      `子供の年齢の下限値が上限値より大さいです。下限値: ${min}, 上限値: ${max}`
    )
  }

  return {
    min: minAge,
    max: maxAge,
  }
}

const mapId = (value: string) => {
  if (value === '0') return null
  if (isNaN(Number(value))) {
    throw new Error(`IDの値が不正です。${value}`)
  }
  return value
}

const diagnosisTypes: Record<keyof typeof DiagnosisType, undefined> = {
  A: undefined,
  B: undefined,
  C: undefined,
  D: undefined,
  E: undefined,
  F: undefined,
  G: undefined,
  H: undefined,
} as const

const isDiagnosisType = (v: string): v is DiagnosisType => v in diagnosisTypes

const diagnosisType = (
  value: string | null | undefined
): InputMaybe<DiagnosisType> => {
  if (!value || value === 'なし') return null

  if (isDiagnosisType(value)) return value

  const allDiagnosisTypes = Object.keys(diagnosisTypes)
  const availableValues = ['なし', ...allDiagnosisTypes].join(', ')
  throw new Error(
    `診断タイプの値が不正です。${value}。有効な値: ${availableValues}`
  )
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
const checkMissingKeys = <T extends {}>(data: T[]): keyof CSVData => {
  const [first] = data
  if (first == null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [] as any
  }

  const notRequiredKeys = ['診断タイプ']
  const targetKeys = CSV_KEYS.filter(key => !notRequiredKeys.includes(key))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return targetKeys.filter(key => !Object.hasOwn(first, key)) as any
}

export const mapCsvStringToAttackListSenderSetting = (
  csvString: string
): Maybe<CreateAttackListSenderSettingsInput> => {
  const { data } = papaparse.parse<CSVData>(csvString, {
    header: true,
    // If set to 'greedy', lines that don't have any content (those which have only whitespace after parsing) will also be skipped.
    // refs: https://www.papaparse.com/docs
    skipEmptyLines: 'greedy',
  })

  // NOTE: step関数で検査するとparse results消し飛ばしちゃうのでここまで型が保証されないので注意
  const missingKeys = checkMissingKeys(data)
  if (missingKeys.length > 0) {
    throw new Error(
      `1行目に[${missingKeys}]がありません。取り込みを中断しました。`
    )
  }

  const enabledData = data.filter(value => value['ステータス'] === '設定中')

  // メールでバリデーションする
  // 400F以外のメールアドレスだったらエラーとする
  const emails = enabledData.map(value => value['OAのメールアドレス'])
  validateEmail(emails)

  const settings = enabledData.map<CreateAttackListSenderSettingsInputSetting>(
    value => ({
      plannerEmail: value['OAのメールアドレス'],
      isEnabled: true,
      title: value['テンプレタイトル'],
      rule: {
        savingRange: mapValueRange(
          value['預貯金下限'],
          value['預貯金上限'],
          '預貯金'
        ),
        annualIncomeRange: mapValueRange(
          value['本人年収下限'],
          value['本人年収上限'],
          '本人年収'
        ),
        familyAnnualIncomeRange: mapValueRange(
          value['世帯年収下限'],
          value['世帯年収上限'],
          '世帯年収'
        ),
        partner: mapExistence(value['パートナー']),
        child: mapExistence(value['子供']),
        ageOfFirstChildRange: mapChildAgeRange(
          value['一人目の子供の年齢下限'],
          value['一人目の子供の年齢上限']
        ),
        gender: mapGender(value['性別']),
        shouldSendIfSameCorporationApproached: mapSameCorporationApproach(
          value['同一企業 追いアプローチ']
        ),
        investmentRange: mapValueRange(
          value['資産運用額下限'],
          value['資産運用額上限'],
          '資産運用額'
        ),
        age: mapAgeRange(value['年齢下限'], value['年齢上限']),
        jobId: mapId(value['職業ID']),
        loanBalanceRange: mapValueRange(
          value['ローン残債下限'],
          value['ローン残債上限'],
          'ローン残債'
        ),
        ageOfYoungestChildRange: mapChildAgeRange(
          value['末子の年齢下限'],
          value['末子の年齢上限']
        ),
        karteTopicId: mapId(value['気になるトピックID']),
        diagnosisType: diagnosisType(value['診断タイプ']),
      },
      body: mapApproachBody(value['アプローチ本文']),
      selections: mapSelections(value),
    })
  )

  return {
    settings,
  }
}
